import { heroStats, programStats } from '~/data/stats'
import { trustedCompany } from '~/data/trustedCompanies'
import { solutions } from '~/data/solutions'
import { type CourseRoleId, courseRoles } from '~/data/courseRoles'
import {
  type CourseId,
  type Course,
  type CourseLevel,
  courses,
  defaultCourseOrder
} from '~/data/courses'
import {
  courseGroupByRole,
  featuredCourses,
  courseGroupByProgram,
  courseGroupByType
} from '~/data/courseGroups'
import { type CourseProviderId, courseProviders } from '~/data/courseProviders'
import { testimonies, type TestimonialId } from '~/data/testimonies'
import { type Program, programs, type ProgramId } from '~/data/programs'
import { type ProgramPartnerId, programPartners } from '~/data/programPartners'
import { courseTypes, type CourseTypeId } from '~/data/courseTypes'
import { courseTools, type CourseToolId } from '~/data/courseTools'
import { teamMembers, type TeamMemberId } from '~/data/teamMembers'
import { courseFAQs } from '~/data/courseFaqs'
import { enrolments, type Enrolment } from '~/data/enrolments'
import { jobs, locations } from '~/data/jobs'
import { employers } from '~/data/employers'
import { jobCampaigns } from '~/data/jobCampaigns'
import { events } from '~/data/events'
import { certificates } from '~/data/certificates'
import { students, type StudentRankItem, type StudentSkillMap } from '~/data/students'
import {
  adminForumSidebarLinks,
  jobStudentSidebarLinks,
  programmesStudentSidebarLinks,
  publicForumSidebarLinks,
  studentForumSidebarLinks,
  studentSidebarLinks,
  type StudentNavbarTitle,
  studentNavbarLinks,
  jobAdminNavbarLinks,
  adminSidebarLinks,
  type AdminNavbarTitle
} from '~/data/studentNavs'
import { rankTests } from '~/data/ranks'
import type { components } from '~/types/napis'
import { languages } from '~/data/languages'
import type { ProfileCertCardItem } from '~/data/profile'
import { notiTypeContent, tempNotifications } from '~/data/notifications'
import {
  tempForumAnnouncements,
  tempForumCategory,
  tempForumComments,
  tempForumPost,
  tempForumReports,
  tempForumTags,
  tempForumUsers,
  tempForumReplies,
  type ForumAuthor,
  forumTagSlugs
  // type ForumComment
} from '~/data/forum'
import {
  classForumStudentTabs,
  forumBookmarkNavs,
  forumHistoryNavs,
  forumTagCategoryBreadcrumbs
} from '~/data/forumNavs'
import type { ForumPost } from '~/data/forumPosts'
import { classGradeLessonTypes, tempClassModules } from '~/data/classroom'
import { formOptions, type EventRegistrationFormId } from '~/data/eventForms'
import {
  interviewFeedbackCategoriesScore,
  type InterviewFeedbackCategoryTitle
} from '~/data/interview-feedback'
import { interviewTermsAndConditions } from '~/data/tempInterviews'
import { programmeRegistrationForms } from '~/data/programmeForms'
import { cv_grade_weightage, type Cv_Feedback_Grading_Category } from '~/data/cv'
import { testTermsAndConditions } from '~/data/tempTests'

function getProgrammeStats() {
  return programStats
}

function getHeroStats() {
  return heroStats
}

function getTrustedCompanies() {
  return trustedCompany
}

function getSolutions() {
  return solutions
}

// ---------------------------
// CV
// ---------------------------

const getCvScoreWeightage = (title: Cv_Feedback_Grading_Category) => {
  return cv_grade_weightage[title]
}

// ---------------------------
// COURSES
// ---------------------------

function getCourseById(courseId: CourseId): Course | undefined {
  /*
  Given a courseId, return the course object.
  */
  return courses.find((course) => course.id === courseId)
}

function getCourseTypeById(courseId: CourseId): CourseTypeId | undefined {
  /*
  Given a courseId, return the type of the course.
  */
  if (courseGroupByType.ca.includes(courseId)) {
    return 'ca'
  }
  if (courseGroupByType.fc.includes(courseId)) {
    return 'fc'
  }
  if (courseGroupByType.m1.includes(courseId)) {
    return 'm1'
  }
  if (courseGroupByType['special-foundation'].includes(courseId)) {
    return 'special-foundation'
  }
  return undefined
}

function getCourseTypeTitle(courseTypeId: CourseTypeId) {
  /*
  Given a courseTypeId, return the title of the course type.
  */
  return courseTypes.find((courseType) => courseType.id === courseTypeId)?.title
}

const getTotalCourses = () => {
  return courses.length
}

function listCourses(
  page: number,
  pageSize: number,
  search: string,
  roles: CourseRoleId[],
  onlyFeatured: boolean,
  levels: CourseLevel[],
  programs: ProgramId[]
) {
  let targetCourseIds: CourseId[] = []
  // Filtered courses based roles
  roles.forEach((role) => {
    targetCourseIds = targetCourseIds.concat(courseGroupByRole[role])
  })
  // Filtered courses based on featured
  if (onlyFeatured) {
    targetCourseIds = targetCourseIds.filter((courseId) => featuredCourses.includes(courseId))
  }
  // filtered courses based on Level
  levels.forEach((level) => {
    targetCourseIds = targetCourseIds.concat(
      courses.filter((course) => course.level === level).map((course) => course.id)
    )
  })
  // filtered courses based on program
  programs.forEach((program) => {
    targetCourseIds = targetCourseIds.concat(
      courseGroupByProgram[program].map((courseId) => courseId)
    )
  })

  // actually filter the courses
  let filteredCourses = courses.filter((course) => course.feature)
  if (roles.length > 0 || onlyFeatured || levels.length > 0 || programs.length > 0) {
    filteredCourses = courses.filter(
      (course) => targetCourseIds.includes(course.id) && !course.hidden
    )
  }

  // Search coruses when search is not empty
  let searchedCourses = filteredCourses
  if (search.length > 0) {
    searchedCourses = courses.filter((course) => {
      return course.title.toLowerCase().includes(search.toLowerCase())
    })
  }

  // Sort courses by defaultCourseOrder
  searchedCourses = searchedCourses.sort((a, b) => {
    return defaultCourseOrder.indexOf(a.id) - defaultCourseOrder.indexOf(b.id)
  })
  // Sort courses by level
  // const levelMap: Record<CourseLevel, number> = {
  //   Beginner: 1,
  //   Intermediate: 2,
  //   Advanced: 3
  // }
  // if (orderdBy === 'level') {
  //   searchedCourses = searchedCourses.sort((a, b) => {
  //     return levelMap[a.level] - levelMap[b.level]
  //   })
  // }

  // Only paginate if pageSize is greater than 0
  let pagedCourses = searchedCourses
  if (pageSize > 0) pagedCourses = searchedCourses.slice((page - 1) * pageSize, page * pageSize)

  return {
    courses: pagedCourses,
    page,
    pageSize,
    total: searchedCourses.length,
    numPages: Math.ceil(searchedCourses.length / pageSize)
  }
}

function getCourseRoles() {
  return courseRoles
}

function getCourseRolesById(courseId: CourseId) {
  /*
  Given a courseId, return the roles of the course.
  */
  return courseRoles.filter((courseRole) => courseGroupByRole[courseRole.id].includes(courseId))
}

function getCourseRoleTitle(role: CourseRoleId) {
  /*
  Given a role, return the title of the role.
  */
  return courseRoles.find((courseRole) => courseRole.id === role)?.title
}

function getCourseProviderTitle(providerId: CourseProviderId) {
  /* 
  Given a providerId, return the title of the provider.
  */
  return courseProviders.find((provider) => provider.id === providerId)?.title
}

function getCourseProviderTitleByCourseId(courseId: string) {
  if (courseId === 'capt') {
    return 'PwC & TalentLabs'
  } else if (
    courseId === 'gcc-aie' ||
    courseId === 'gcc-its' ||
    courseId === 'gcc-ita' ||
    courseId === 'gcc-da' ||
    courseId === 'gcc-pm' ||
    courseId === 'gcc-ud' ||
    courseId === 'gcc-dme' ||
    courseId === 'gcc-ada' ||
    courseId === 'gcc-bi' ||
    courseId === 'gcc-cs'
  ) {
    return 'Google & TalentLabs'
  } else return 'TalentLabs'
}

function getTestimonies() {
  return testimonies
}

function getTestimoniesByIds(ids: TestimonialId[]) {
  return testimonies.filter((testimony) => ids.includes(testimony.id))
}

function getCourseToolById(courseToolId: CourseToolId | string): {
  title: string
  id: CourseToolId
  skill: boolean
} | null {
  const foundCourseTool = courseTools.find(
    (courseTool) => courseTool.id === courseToolId.toLowerCase()
  )

  if (foundCourseTool) {
    return foundCourseTool
  } else {
    return null
  }
}

function getCourseFAQs(query: string) {
  return courseFAQs.filter((faq) =>
    faq.type.toLocaleLowerCase().includes(query.toLocaleLowerCase())
  )
}

function getEnrolmentsByCourseId(courseId: CourseId) {
  let items: Enrolment[] = []

  enrolments.forEach((e) => {
    if (e.courses.find((course) => course === courseId)) items = items.concat([e])
  })

  return items
}

const getCourseByName = (courseName: string) => {
  const course = courses.find((course) => course.title === courseName)
  if (course) return course

  // fallback course
  // foundation cert
  if (courseName.includes('FC') || courseName.includes('Foundation Certificate')) {
    if (courseName.includes('Data-driven')) return getCourseById('fctm')
  }
  // certified associate
  else if (courseName.includes('CA') || courseName.includes('Certified Associate')) {
    if (courseName.includes('Data Science')) return { ...getCourseById('cada'), id: 'cads' }
    else if (courseName.includes('Artificial Intelligence')) return getCourseById('cada')
    else if (courseName.includes('Software Engineering'))
      return { ...getCourseById('cafed'), id: 'case' }
  }

  // else
  else return undefined
}

function getBadgeByCourseName(courseName: string) {
  if (!courseName) return ''
  if (courseName.includes('Fintech')) {
    return 'fif'
  } else if (
    courseName.includes('FC') ||
    courseName.includes('Foundation Certificate') ||
    courseName.includes('FOUNDATION') ||
    courseName.includes('Fundamental')
  ) {
    if (courseName.includes('Data Analytics')) {
      return 'fcda'
    } else if (courseName.includes('Digital Marketing')) {
      return 'fcdm'
    } else if (courseName.includes('Software Engineering')) {
      return 'fcse'
    } else if (courseName.includes('Technology Management')) {
      return 'fctm'
    } else if (courseName.includes('Data-driven')) {
      return 'fctm'
    } else if (courseName.includes('UX')) {
      return 'fcuiux'
    }
  } else if (
    courseName.includes('CA') ||
    courseName.includes('Certified Associate') ||
    courseName.includes('CERTIFIED_ASSOCIATE')
  ) {
    if (courseName.includes('Business Analytics')) {
      return 'caba'
    }
    if (courseName.includes('Back-end Development')) {
      return 'cabed'
    }
    if (courseName.includes('Data Analytics')) {
      return 'cada'
    }
    if (courseName.includes('Data Science')) {
      return 'cada'
    }
    if (courseName.includes('Artificial Intelligence')) {
      return 'cada'
    }
    if (courseName.includes('Front-end Development')) {
      return 'cafed'
    }
    if (courseName.includes('Penetration Testing')) {
      return 'capt'
    }
    if (courseName.includes('Software Engineering')) {
      return 'case'
    }
  } else if (courseName.includes('M1') || courseName.includes('OTHER')) {
    if (courseName.includes('Python')) {
      return 'm1py'
    } else if (courseName.includes('SQL')) {
      return 'm1sql'
    } else if (courseName.includes('Javascript')) {
      return 'm1js'
    } else if (courseName.includes('Blockchain')) {
      return 'm1blockchain'
    }
  }
}

const getDashboardCourses = () => {
  return ['cada', 'capt'].map((id) => getCourseById(id as CourseId))
}

// ---------------------------
// PROGRAMMES
// ---------------------------

function getPrograms(): Program[] {
  return programs
}

function getProgramById(programId: ProgramId): Program | undefined {
  /*
  Given a programId, return the program object.
  */
  return programs.find((program) => program.id === programId)
}

function getProgramPartnerTitle(partnerId: ProgramPartnerId) {
  /*
  Given a programId, return the partner title of the program.
  */
  return programPartners.find((partner) => partner.id === partnerId)?.title
}

const getActivePrograms = () => {
  /*
    For dashboard, give programmes with open application. Max 2 programmes
    */
  //   return []
  return programs
    .filter((program) => program.applicationOpen && program.showAtDashboard)
    .slice(0, 2)
}

// ---------------------------
// NAVIGATIONS
// ---------------------------

// ForumLayout
// return appropriate forum sidebar links
function getForumSidebarLinks(isAdmin: boolean, isAuthenticated: boolean) {
  return isAuthenticated
    ? isAdmin
      ? adminForumSidebarLinks
      : studentForumSidebarLinks
    : publicForumSidebarLinks
}

function getProgrammeSidebarLinks() {
  return programmesStudentSidebarLinks
}

function getStudentSidebarLinks(title: string, role: string) {
  if (role === 'candidate') return studentSidebarLinks[title as StudentNavbarTitle]
  else return adminSidebarLinks[title as AdminNavbarTitle]
}

const getStudentNavbarLinks = (user: ReturnType<typeof useAuth>['data']['value']) => {
  const role = getPrimaryUserRole(user)
  if (role !== 'candidate') return jobAdminNavbarLinks
  return studentNavbarLinks
}

function getCourseFromProgram(id: string) {
  const program = programs.find((p) => p.id === id)
  if (program) {
    const programCourses = program.sections?.courses?.courses

    if (programCourses) {
      return programCourses.map((course) => {
        const courseDetail = courses.find((c) => c.id === course.id)

        return {
          id: courseDetail?.id as string,
          title: courseDetail?.title
        }
      })
    }

    return []
  }
  return []
}

function getProgrammeRegistrationFormOptions(id: ProgramId) {
  return programmeRegistrationForms[id]
}

// ---------------------------
// TEAMS
// ---------------------------

function getTeamMembers() {
  return teamMembers
}

function getFounders() {
  return teamMembers.filter((teamMember) => teamMember.position.toLowerCase().includes('founder'))
}

function getTeamMemberById(teamMemberId: TeamMemberId) {
  return teamMembers.find((teamMember) => teamMember.id === teamMemberId)
}

// ---------------------------
// JOBS
// ---------------------------

function getJobById(jobId: string) {
  return jobs.find((job) => job.id === jobId)
}

function listJobs(
  page: number,
  pageSize: number,
  search: string,
  applicationId: string,
  country: string,
  roles: CourseRoleId[],
  remote: string,
  type: string
) {
  // Filter jobs
  const filteredJobs = jobs.filter((job) => {
    return (
      // Filter jobs based on application type
      // if job application regular, then only include non-exlusive jobs
      (applicationId === 'ja-regular'
        ? job.applications.findIndex((a) => !jobCampaigns.find((ja) => ja.id === a)?.exclusive) >= 0
        : // if not, then include the ones with similar application id
          job.applications.findIndex((a) => a === applicationId) >= 0) &&
      // Filter jobs based on career role
      (roles.length > 0 ? roles.findIndex((r) => r === job.role) >= 0 : true) &&
      // Filter jobs based on country
      (country ? job.location.toLowerCase().includes(country.toLowerCase()) : true) &&
      // Filter jobs based on remote
      (remote ? job.remote === remote : true) &&
      // Filter jobs based on type
      (type ? job.type === type : true) &&
      // Filter jobs based on search keyword
      (search.length > 0 ? job.title.toLowerCase().includes(search.toLowerCase()) : true)
    )
  })

  // Only paginate if pageSize is greater than 0
  const pagedJobs =
    pageSize > 0 ? filteredJobs.slice((page - 1) * pageSize, page * pageSize) : filteredJobs

  return {
    jobs: pagedJobs,
    total: filteredJobs.length,
    page,
    pageSize,
    numPages: Math.ceil(filteredJobs.length / pageSize)
  }
}

function getSimilarJobs(jobRole: string) {
  console.log(jobRole)
  return jobs.filter((job) => job.role === jobRole).slice(0, 3)
}

function getEmployerJobs(jobId: string, employerId: string) {
  return jobs.filter((job) => job.id !== jobId && job.employer === employerId).slice(0, 3)
}

function getFeaturedJobs() {
  return jobs.filter((job) => job.featured)
}

function getEmployerById(id: string) {
  const employer = employers.find((employer) => employer.id === id.toLowerCase())
  if (!employer) return null
  return employer
}

// return true if the job has applications that are currently active & exclusive
function getExclusiveJobCampaigns(apps: string[]) {
  return jobCampaigns.find((j) => apps.find((a) => a === j.id) && j.active && j.exclusive)
}

function getAllActiveJobCampaigns() {
  return jobCampaigns.filter((j) => j.active)
}

function getJobSidebarLinks() {
  return jobStudentSidebarLinks
}

// ---------------------------
// TEMPORARY JOB FUNCTIONS
// ---------------------------

function getSavedJobs(page: number, pageSize: number) {
  // temp jobs
  const savedJobs = jobs.filter((job) => job.featured)

  // Only paginate if pageSize is greater than 0
  const pagedJobs =
    pageSize > 0 ? savedJobs.slice((page - 1) * pageSize, page * pageSize) : savedJobs

  return {
    jobs: pagedJobs,
    page,
    numPages: Math.ceil(savedJobs.length / pageSize)
  }
}

function getAppliedJobs(page: number, pageSize: number) {
  // temp jobs
  const appliedJobs = jobs.filter((job) => job.featured)

  // Only paginate if pageSize is greater than 0
  const pagedJobs =
    pageSize > 0 ? appliedJobs.slice((page - 1) * pageSize, page * pageSize) : appliedJobs

  return {
    jobs: pagedJobs,
    page,
    numPages: Math.ceil(appliedJobs.length / pageSize)
  }
}

function checkSavedJob(userId: string | undefined, jobId: string) {
  // if no user then no saved job
  if (!userId || userId === '') return false

  // debugging purposes
  return parseInt(jobId.replace('job-', '')) % 3 === 0
}

function checkAppliedJob(userId: string | undefined, jobId: string) {
  // if no user then no applied job
  if (!userId || userId === '') return false

  // debugging purposes
  return parseInt(jobId.replace('job-', '')) % 2 !== 0
}

function getResume(userId: string) {
  // if no user then
  if (userId === '') return false

  return true
}

// ---------------------------
// EVENTS
// ---------------------------

function getFeaturedEvents() {
  return events.filter((event) => event.featured)
}

function listEvents(type: string, country: string, mode: string) {
  return events.filter((event) => {
    return (
      (type ? event.type.toLowerCase() === type.toLowerCase() : true) &&
      (!country
        ? true
        : event.location
          ? event.location.country.toLowerCase() === country.toLowerCase()
          : false) &&
      (mode ? event.mode.toLowerCase() === mode.toLowerCase() : true)
    )
  })
}

function getEventById(id: string) {
  return events.find((event) => event.id === id)
}

const getPromotedEvent = () => {
  return events.find((event) => event.showAtDashboard)
}

const getEventFormOptions = (id: EventRegistrationFormId) => {
  return formOptions[id]
}

// ---------------------------
// CERTIFICATE
// ---------------------------

function getCertificateById(id: string) {
  return certificates.find((cert) => cert.id === id)
}

function getStudentById(id: string) {
  return students.find((student) => student.id === id)
}

// ---------------------------
// PROFILE
// ---------------------------

// Get rank test by id
function getRankTestById(id: string) {
  return rankTests.find((test) => test.id === id)
}

// put 'beginner' and 'intermediate' tests of the same topic under
// the same item, the score averaged out
// rearrange according to score
const getRankingsWithoutDuplicates = (rankings: StudentRankItem[]) => {
  let topics = {}
  const newRankings: StudentRankItem[] = []

  rankings.forEach((r) => {
    const mainId = r.testId.split('-')[0]

    // if topic is new
    if (!(mainId in topics)) {
      // add topic
      topics = {
        [mainId]: r.testId,
        ...topics
      }
      // add ranking item
      newRankings.push(r)
    }
    // if already has topic
    else {
      // get test from array, add scores, remove old, add new
      // @ts-ignore
      const id = topics[mainId]
      const i = newRankings.findIndex((nr) => nr.testId === id)
      const newScores = r.scores.concat(newRankings[i].scores)
      newRankings.splice(i, 1)
      newRankings.push({
        ...r,
        scores: newScores
      })
    }
  })

  // rearrange the ranking according to scores
  const getAvg = (scores: number[]) => {
    let total = 0
    scores.forEach((s) => (total += s))
    return total / scores.length
  }
  return newRankings.sort((a, b) => getAvg(b.scores) - getAvg(a.scores))
}

// Sort skills map from highest to lowest
const rearrangeSkillsMap = (skills: StudentSkillMap[]) => {
  // rearrange the ranking according to scores
  const getAvg = (breakdown: Array<{ score: number }>) => {
    let total = 0
    breakdown.forEach((b) => (total += b.score))
    return total / breakdown.length
  }
  return skills.sort((a, b) => getAvg(b.breakdown) - getAvg(a.breakdown))
}

// Get location filters
const getLocationOptions = () => {
  // Each location contains multiple levels, the levels are separated by '|'
  return locations.map((location) => {
    return {
      title: getReadableJobLocation(location) || location,
      id: location
    }
  })
}

const getRegionOptions = () => {
  // Each location contains multiple levels, the levels are separated by '|'
  // We will extract the last level of each location as the region
  let regions = locations.map((location) => {
    const levels = location.split('|')
    return levels[levels.length - 1]
  })
  regions = [...new Set(regions)]
  return regions.map((region) => {
    return {
      title: getReadableJobLocation(region) || region,
      id: region
    }
  })
}

// Get skills options - for profile filter
const getSkillsOptions = () => {
  return courseTools
    .filter((c) => c.skill)
    .map((c) => c.title)
    .sort()
}

// Change tool from id to title
const mapToolToTitle = (id: CourseToolId | string) => {
  const tool = courseTools.find((c) => c.id === id)
  return tool ? tool.title : id
}

// Change tool from title to id
const mapToolToId = (title: string) => {
  const tool = courseTools.find((c) => c.title === title)
  return tool ? tool.id : ''
}

// Get skill / tool array in title form
const getStudentChosenTools = (tools: CourseToolId[] | string[]) => {
  return tools.map((tool) => mapToolToTitle(tool))
}

// Get work experience item by id
const getWorkExperienceById = (id: string) => {
  const student = getStudentById('student-0')
  if (!student) return null
  // check work experiences owned by current student
  return student?.profile.about.workExperiences.find((w) => w.id === id)
}

// Get education background item by id
const getEduBackgroundById = (id: string) => {
  const student = getStudentById('student-0')
  if (!student) return null
  // check education background owned by current student
  return student?.profile.about.educations.find((w) => w.id === id)
}

// Get highest ranking for profile header
const getHighestRanking = (ranking: StudentRankItem[]) => {
  // return negative number if no ranking
  if (ranking.length === 0) return -1

  let highestRank = 10000 // temp high number

  ranking.forEach((rank) => {
    highestRank = rank.rank < highestRank ? rank.rank : highestRank
  })

  return highestRank
}

// get certificates array from array of cert id
const getCertificatesListById = (ids: string[]) => {
  return ids.map((id) => getCertificateById(id))
}

const calculateProfileCompletion = (
  profile: components['schemas']['UserProfileResponseSchema']
) => {
  let filled = 0
  const tasks = []
  const linkOverview = '/profile/edit?form=overview'
  const linkWork = '/profile/edit?form=work'
  const linkEdu = '/profile/edit?form=education'
  const linkContact = '/profile/edit?form=contact'

  // overview section
  if (profile.about) {
    if (profile.about.bio) {
      filled += 1
    } else {
      tasks.push({
        title: 'Add your bio under Overview',
        link: linkOverview
      })
    }
    if (profile.about.employment_status) {
      filled += 1
    } else {
      tasks.push({
        title: 'Add your employment status under Overview',
        link: linkOverview
      })
    }
    if (profile.about.profile_languages) {
      filled += 1
    } else {
      tasks.push({
        title: 'Add your languages under Overview',
        link: linkOverview
      })
    }
    if (profile.about.work_permit_locations && profile.about.work_permit_locations.length > 0) {
      filled += 1
    } else {
      tasks.push({
        title: 'Add your work permit locations under Overview',
        link: linkOverview
      })
    }
    if (profile.about.skills && profile.about.skills.length > 0) {
      filled += 1
    } else {
      tasks.push({
        title: 'Add your skills under Overview',
        link: linkOverview
      })
    }

    // contact
    if (profile.about.profile_location) {
      filled += 1
    } else {
      tasks.push({
        title: 'Add your location under Contact',
        link: linkContact
      })
    }
    if (profile.about.links && profile.about.links.linkedin_link) {
      filled += 1
    } else {
      tasks.push({
        title: 'Add your Linkedin link under Contact',
        link: linkContact
      })
    }
  }

  // work experiences
  if (profile.work_experience_data && profile.work_experience_data.length > 0) {
    filled += 1
  } else {
    tasks.push({
      title: 'Add your work experiences',
      link: linkWork
    })
  }

  // education
  if (profile.education_data && profile.education_data.length > 0) {
    filled += 1
  } else {
    tasks.push({
      title: 'Add your education background',
      link: linkEdu
    })
  }

  const total = 9
  return {
    progress: filled / total,
    tasks
  }
}

const statusOptions = [
  {
    title: 'Employed, not looking for a job',
    id: 'EMPLOYED_AND_NOT_LOOKING_FOR_JOB'
  },
  {
    title: 'Employed, looking for a job',
    id: 'EMPLOYED_BUT_LOOKING_FOR_JOB'
  },
  {
    title: 'Unemployed, looking for a job',
    id: 'UNEMPLOYED_AND_LOOKING_FOR_JOB'
  },
  {
    title: 'Unemployed, not looking for a job',
    id: 'UNEMPLOYED_BUT_NOT_LOOKING_FOR_JOB'
  }
]

const getLanguagesOptions = () => {
  return languages
}

const getEmploymentStatusOptions = () => {
  return statusOptions
}

const mapCertificates = (
  graduationProofs: components['schemas']['GraduationProofResponseSchema'][]
) => {
  if (!graduationProofs || graduationProofs.length === 0) return []

  const mappedProofs = graduationProofs.map(
    (cert: components['schemas']['GraduationProofResponseSchema']) => {
      let title = ''
      const date = cert.graduation_date
      if (cert.certificate) title = cert.certificate.title ?? ''
      else if (cert.course) title = cert.course.title ?? ''

      // Convert date string to Date object if both title and date exist
      return title && date ? { gradId: cert.id_slug, title, date: new Date(date) } : undefined
    }
  )
  const filtered = mappedProofs.filter((cert: any) => cert !== undefined)

  return filtered as ProfileCertCardItem[]
}

const listCertificates = (
  currentPage: number,
  itemsPerPage: number,
  certificates: ProfileCertCardItem[],
  totalPage: number
) => {
  const start = (currentPage - 1) * itemsPerPage
  const end = start + itemsPerPage

  return {
    totalPage,
    items: certificates.slice(start, end)
  }
}

const removeEmptyArrayItems = (array: any) => {
  return array.filter((n: any) => {
    if (typeof n === 'string') return n !== ''
    else return n
  })
}

// ---------------------------
// NOTIFICATIONS FUNCTIONS
// ---------------------------

// TEMPORARY - BACKEND WILL REPLACE THIS
// Get sorted notifications list, with pagination size = 7
const getNotificationsData = (userId: string, page: number) => {
  console.log(userId)

  // sort 1 - according to latest date
  const sorted = tempNotifications.sort(
    (a, b) => new Date(b.created_date).getTime() - new Date(a.created_date).getTime()
  )

  // sort 2 - unread notifications first, then read
  const read = sorted.filter((noti) => noti.is_read)
  const unread = sorted.filter((noti) => !noti.is_read)
  const all = unread.concat(read)

  // add pagination
  const size = 7 // 7 notifications per page
  const total = Math.ceil(all.length / size) // total pages
  const data = all.slice((page - 1) * size, page * size)

  return {
    page, // current page
    total, // total pages
    data, // the rendered data
    totalUnread: unread.length
  }
}

// UTILITY FUNCTION - DO NOT DELETE
// Return notification title and description
const getNotiContent = (type: string) => {
  const content = notiTypeContent[type as keyof typeof notiTypeContent]

  // if user is the same as
  return content
}

// UTILITY FUNCTION - DO NOT DELETE
// check notification is a report type or not
const isNotiReportType = (type: string) => {
  return (
    type === 'REPORT_SUBMITED' || type === 'CONTENT_REPORTED_HIDDEN' || type === 'CONTENT_DELETED'
  )
}

// UTILITY FUNCTION - DO NOT DELETE
// check notification is a report type or not
const isNotiClassType = (type: string) => {
  return type === 'NEW_SUPPORT_POST' || type === 'NEW_ANNOUNCEMENT'
}

// UTILITY FUNCTION - DO NOT DELETE
// To return user name, to be shown in notification text, if necessary
const getNameInNoti = (noti: components['schemas']['NotificationSchema']) => {
  // if notification is of the report type, then do not send in author name info
  if (isNotiReportType(noti.notification_type)) return ''

  // get the post or comment author
  const author = noti.post?.author || noti.comment_reply?.author || null

  // if author is null, return empty string
  if (!author) return ''
  // if not, return the author's full name
  else return `${author.first_name} ${author.last_name}`
}

// UTILITY FUNCTION - DO NOT DELETE
// To return user image url, to be shown in notification item, if necessary
const getImageUrlInNoti = (noti: components['schemas']['NotificationSchema']) => {
  // if notification is of the report type, then do not send in author image info
  if (isNotiReportType(noti.notification_type)) return ''

  // if notification is of the class type, then the image is the tag imageUrl
  if (isNotiClassType(noti.notification_type)) {
    const tempTagImage = '/images/landing/landing-page/community/landing-community-31.png'
    const hasClassroomTag = noti.post?.tags?.some(
      (tag) => tag.category.category_name === 'CLASSROOM'
    )
    return hasClassroomTag ? tempTagImage : ''
  }

  // if notification is of post type, then the image is the author
  if (noti.post) return noti.post?.author.profile_image?.presigned_get_object_url
  else return ''
}

// UTILITY FUNCTION - DO NOT DELETE
// To return post title which will be shown in the notification text
const getPostTitleInNoti = (noti: components['schemas']['NotificationSchema']) => {
  return (
    // return post title
    noti.post?.title ||
    // return post title of the comment or reply is in
    noti.comment_reply?.post_title ||
    // if report, return reported post or comment itself
    noti.report?.post?.title ||
    (noti.report?.comment_reply?.description
      ? // if comment is long, then return a shortened comment
        noti.report?.comment_reply?.description.length > 50
        ? noti.report?.comment_reply?.description.slice(0, 50).concat('...')
        : noti.report?.comment_reply?.description
      : '') ||
    ''
  )
}

// UTILITY FUNCTION - DO NOT DELETE
// To return an appropriate description based on the user's content
const getNotiDescription = (content: any, noti: components['schemas']['NotificationSchema']) => {
  const type = noti.notification_type
  const author = noti.post?.author || noti.comment_reply?.author || null

  // if there is no author name involved, return normal description
  if (!author) return content.description

  // check notification type if there is a possibiility of a different desciption
  if (type === 'NEW_COMMENT' || type === 'POST_UPDATED' || type === 'POST_HAVE_ANSWER') {
    // only return description according to user and author's similarity
    return author === noti.user ? content.description_your : content.description
  }

  // return original description if the content only has original one
  else return content.description
}

// UTILITY FUNCTION - DO NOT DELETE
// To return notification title and notification text
const replaceNotiContent = (noti: components['schemas']['NotificationSchema']) => {
  const content = getNotiContent(noti.notification_type)
  const title = content.title

  const description = getNotiDescription(content, noti)
    .replaceAll('AUTHOR_NAME', getNameInNoti(noti))
    .replaceAll('POST_TITLE', getPostTitleInNoti(noti))
    .replaceAll('CLASS_TITLE', noti.post?.tags ? noti.post?.tags[0].name : '')

  return { title, description }
}

// UTILITY FUNCTION - DO NOT DELETE
// To return the page link when notification is clicked
const getNotiPageLink = (noti: components['schemas']['NotificationSchema']) => {
  // if notification is of the report type, then the notification has no link
  if (isNotiReportType(noti.notification_type)) return ''

  // if notification is of the class announcement type, then the notification will navigate to class
  if (noti.notification_type === 'NEW_ANNOUNCEMENT' && noti.post?.tags)
    return `/forum/class-forum/${noti.post?.tags[0].slug_name}`

  // If not, then the link will navigate to the post
  // Get post id and comment id
  let postIdSlug = null
  let commentIdSlug = null
  if (noti.post) {
    postIdSlug = noti.post.id_slug
    commentIdSlug = null
  } else if (noti.comment_reply) {
    postIdSlug = noti.comment_reply.post_id_slug
    commentIdSlug = noti.comment_reply.id_slug
  } else if (noti?.report?.post) {
    postIdSlug = noti.report.post.id_slug
    commentIdSlug = null
  } else if (noti?.report?.comment_reply) {
    postIdSlug = noti.report.comment_reply.post_id_slug
    commentIdSlug = noti.report.comment_reply.id_slug
  }

  const postId = postIdSlug || ''
  const commentId = commentIdSlug || ''

  // return link if we get post id
  return commentId ? `/forum/post/${postId}#${commentId}` : postId ? `/forum/post/${postId}` : ''
}

// -----------------------------------------------------
// FORUM FUNCTIONS
// -----------------------------------------------------
// Temporary, related to backend
// -----------------------------------------------------

// -------
// ------- POST -------

// /forum/feed
// return posts for Feed page - associated with user follows
const getFeedFollowingPosts = (page: number = 1) => {
  return paginatePosts(page, tempForumPost)
}

// /forum/feed/for-you
// return posts for For You page - associated with user activity
const getFeedForYouPosts = (page: number = 1) => {
  return paginatePosts(page, tempForumPost)
}

// forum/tag/[id]
// forum/class-forum/[id]
// return posts from the tag
const getPostsFromTag = (id: string, page: number = 1) => {
  console.log(id)
  return paginatePosts(page, tempForumPost)
}

// used in component - ForumPost
const setPostAsPinned = (postId: string) => console.log('admin user pin post', postId)

// used in component - ForumPost
const setPostAsUnpinned = (postId: string) => console.log('admin user unpin post', postId)

// used in component - ForumPostMenu
const setPostAsResolved = (postId: string) => console.log('set post resolved', postId)

// used in component - ForumPostMenu
const setPostAsUnresolved = (postId: string) => console.log('set post unresolved', postId)

// used in component - ForumPostMenu
const deleteForumPost = (postId: string) => console.log('post deleted', postId)

// dashboard
const getDashboardPosts = () => tempForumPost.slice(0, 3)

// -------
// ------- CLASS-POST -------

// /forum/class-forum/[slug]
const getClassPostsFromTag = (classSlug: string, page: number = 1) => {
  console.log(classSlug)
  const posts = tempForumPost.filter(
    (post) => post.categories && post.categories[0].category_name === 'CLASSROOM'
  )
  return paginatePosts(page, posts)
}

// /forum/class-forum/[slug]
const getRecentClassAnnouncements = () => {
  return tempForumAnnouncements.slice(0, 3)
}

// /forum/class-forum/[slug]/announcements
const getClassAnnouncements = (classSlug: string, page: number = 1) => {
  console.log(classSlug)
  return paginateAnnouncements(page, tempForumAnnouncements)
}

// /forum/class-forum/[slug]/announcements
// /forum/class-forum/edit-announcement/[id]
// To get one announcement
const getClassAnnouncementFromId = (classSlug: string, announcementId: string) => {
  console.log(classSlug)
  return tempForumAnnouncements.find((ann) => ann.id_slug === announcementId)
}

// /forum/class-forum/[slug]/pinned
const getPinnedPosts = (classSlug: string, page: number = 1) => {
  console.log(classSlug)
  const pinned = tempForumPost
    .filter((post) => post.categories && post.categories[0].category_name === 'CLASSROOM')
    .filter((post) => post.is_pinned)
  return paginatePosts(page, pinned)
}

// Post page - /forum/post/[id]
// Edit page - /forum/post/edit/[id]
// Return forum post from id
// With sorting - the asnwered comment or reply at the top
const getForumPostFromId = (id: string) => {
  const post = { ...tempForumPost.find((post) => post.id_slug === id) }

  return post
}

// -------
// ------- FOLLOW -------

// // used in component - ForumPostMenu
// const followPost = (userId: string, postId: string) =>
//   console.log('user follow post', userId, postId)

// // used in component - ForumPostMenu
// const unfollowPost = (userId: string, postId: string) =>
//   console.log('user unfollow post', userId, postId)

// -------
// ------- COMMENTS -------

// ForumCommentCard
const markCommentAsAnswer = (commentId: string) => console.log('comment is answer', commentId)

// ForumCommentCard
const removeCommentAsAnswer = (commentId: string) => console.log('comment is not answer', commentId)

// Delete comment
const deleteForumComment = (commentId: string) => console.log('comment is deleted', commentId)

// /forum/post/edit/comment/[commmentId]
// /forum/post/[id]
// To return an individual comment
// Use 1 - to edit comment
// Use 2 - if user clicks to the comment from another page, either history, bookmark etc
const getCommentFromId = (commentId: string) => {
  const comment = tempForumComments.find((comment) => comment.id_slug === commentId)
  const reply = tempForumReplies.find((comment) => comment.id_slug === commentId)

  return comment || reply
}

// -------
// ------- TAGS -------

// forum/tag/browse/
// return top 4 tags from each categories
const getBrowseTagsData = () => {
  const tags = tempForumTags

  return {
    general: tags.filter((tag) => tag.category.category_name === 'GENERAL').slice(0, 4),
    skill: tags.filter((tag) => tag.category.category_name === 'SKILL').slice(0, 4),
    career: tags.filter((tag) => tag.category.category_name === 'CAREER').slice(0, 4)
  }
}

// forum/tag/browse/[category]
// return list of tags belonging in that category
const getTagsFromCategory = (name: string) => {
  return tempForumTags.filter((tag) => tag.category.category_name === name)
}

// forum/tag/browse/[category]
// return tag category object from name
const getTagCategoryFromName = (name: string) => {
  return tempForumCategory.find((cat) => cat.category_name === name)
}

// For sidebar - ForumLayout
// Get 3 most active or most recent tags
const getSidebarTags = () => {
  return [tempForumTags[1], tempForumTags[10], tempForumTags[15]]
}

// forum/tag/[slug]
// forum/class-forum/[slug]
// forum/class-forum/[slug]/announcements
// forum/class-forum/[slug]/pinned
// forum/class-forum/[slug]/support
// forum/class-forum/[slug]/moderators
// forum/class-forum/[slug]/students
// Get tag object from slug
const getTagFromName = (slug: string) => {
  const tag = tempForumTags.find((tag) => tag.slug_name === slug)
  return tag
}

// /forum/tag/following
// Get list of tags that user follow
const getFollowingTags = (userId: string) => {
  console.log(userId)
  return tempForumTags.filter((tag) => tag.is_user_follow)
}

// used in component - ForumTagHeader, ForumTagCard
const followTag = (userId: string, tagId: string) => console.log('user follow tag', userId, tagId)

// used in component - ForumTagHeader
const unfollowTag = (userId: string, tagId: string) =>
  console.log('user un follow tag', userId, tagId)

// -------
// ------- TAG-CLASS -------

// For sidebar - ForumLayout
// Get 3 most active or most recent class
const getSidebarClass = () => {
  return tempForumTags.filter((tag) => tag.category.category_name === 'CLASSROOM').slice(0, 3)
}

// /forum/class-forum/my-class-forum
// Return a list of class forums that student is a member of
const getJoinedClassForums = (userId: string) => {
  console.log(userId)
  return tempForumTags.filter((tag) => tag.category.category_name === 'CLASSROOM')
}

// For - admin dashboard
// Return 4 most recent class forums
const getRecentAdminClasses = () => {
  return tempForumTags.filter((tag) => tag.category.category_name === 'CLASSROOM').slice(0, 4)
}

// /forum/class-forum/my-class-forum
// Return all class forums that admin is a member of
const getAllAdminClasses = (userId: string) => {
  console.log(userId)
  return tempForumTags.filter((tag) => tag.category.category_name === 'CLASSROOM')
}

// /forum/class-forum/edit/[slug]
// Return class from slug
const getClassFromSlug = (slug: string) => {
  return tempForumTags.find((tag) => tag.slug_name === slug)
}

// -------
// ------- CLASS STUDENTS & MODS -------

// /forum/class-forum/[id]/moderators
// Return class forum moderators in that class
// with pagination
const getClassForumModerators = (classSlug: string, page: number = 1) => {
  console.log(classSlug)
  return paginateUsers(page, tempForumUsers)
}

// /forum/class-forum/add/[id]/moderators
// Return class forum moderators in that class
// no pagination
const getAllClassForumModerators = (classSlug: string) => {
  console.log(classSlug)
  return tempForumUsers
}

// /forum/class-forum/[id]/studente
// Return class forum students
const getClassForumStudents = (classSlug: string, page: number = 1) => {
  console.log(classSlug)
  return paginateUsers(page, tempForumUsers)
}

// /forum/class-forum/add/[id]/students
// Return class forum students in that class
// no pagination
const getAllClassForumStudents = (classSlug: string) => {
  console.log(classSlug)
  return tempForumUsers
}

// /forum/class-forum/[id]/students
// Delete student from class
const removeStudentFromClass = (studentId: string, classId: string) => {
  console.log('remove student from class', studentId, classId)
}

const addStudentToClass = (studentId: string, classId: string) => {
  console.log('add student from class', studentId, classId)
}

// /forum/class-forum/add/[id]/students
// Add many students to a class
const addManyStudentToClass = (students: string[], classId: string) => {
  console.log('add student from class', students, classId)
}

// /forum/class-forum/[id]/moderators
// Delete moderator from class
const removeModeratorFromClass = (moderatorId: string, classId: string) => {
  console.log('remove mod from class', moderatorId, classId)
}

const addModeratorToClass = (moderatorId: string, classId: string) => {
  console.log('add mod from class', moderatorId, classId)
}

// /forum/class-forum/add/[id]/moderators
// Add many moderators to a class
const addManyModeratorsToClass = (moderatorIds: string[], classSlug: string) => {
  console.log('add mod from class', moderatorIds, classSlug)
}

// /forum/class-forum/add/[id]/moderators
// Search moderators by name or email
const filterModeratorsByName = (keyword: string) => {
  if (keyword === '') return []
  const key = keyword.toLowerCase()

  return tempForumUsers.filter((user) => {
    const firstName = user.first_name.toLowerCase()
    const lastName = user.last_name.toLowerCase()
    const fullName = `${firstName} ${lastName}`
    const email = user.email?.toLowerCase() || ''

    return (
      firstName.includes(key) ||
      lastName.includes(key) ||
      fullName.includes(key) ||
      email.includes(key)
    )
  })
}

// /forum/class-forum/add/[id]/students
// Search students by name or email
const filterStudentsByName = (keyword: string) => {
  if (keyword === '') return []
  const key = keyword.toLowerCase()

  return tempForumUsers.filter((user) => {
    const firstName = user.first_name.toLowerCase()
    const lastName = user.last_name.toLowerCase()
    const fullName = `${firstName} ${lastName}`
    const email = user.email?.toLowerCase() || ''

    return (
      firstName.includes(key) ||
      lastName.includes(key) ||
      fullName.includes(key) ||
      email.includes(key)
    )
  })
}

// -------
// ------- SEARCH -------

// /forum/search/
// Calls when user does a search
const searchForum = (type: string, keyword: string) => {
  // if keyword is empty string, return a null data
  if (keyword === '') return { tags: [], classes: [], posts: [] }

  // get all tags which match the keyword first
  const tags = tempForumTags.filter((tag) => tag.name.toLowerCase().includes(keyword))

  return {
    // return public tags if the type is 'all' or 'tag'
    tags:
      type === 'tag' || type === 'all'
        ? tags.filter((tag) => tag.category.category_name !== 'CLASSROOM')
        : [],

    // return class tags if the type is 'class' or 'tag' or 'all'
    // only show the classes that user follow only
    classes:
      type === 'tag' || type === 'all' || type === 'class'
        ? tags
            .filter((tag) => tag.category.category_name === 'CLASSROOM')
            .filter((tag) => tag.is_user_follow)
        : [],

    // return posts if the type is 'post' or 'tag'
    // return 5 first - temporary - this can change later
    posts:
      type === 'post' || type === 'all'
        ? tempForumPost.filter((post) => post.title.toLowerCase().includes(keyword)).slice(0, 5)
        : []
  }
}

// -------
// ------- REPORT -------

// /forum/report/all
// Return paginated reports
const getAllForumReports = (page: number, showPendingOnly: boolean) => {
  const size = 5
  const reports = showPendingOnly
    ? tempForumReports.filter((report) => report.status === 'pending')
    : tempForumReports
  const total = Math.ceil(reports.length / size) // total pages
  const data = reports.slice((page - 1) * size, page * size)

  return { page, total, data }
}

// ForumReportRow
// get either a post or a comment id
// the content will not be deleted
const deleteReportContent = (postId: string | null, commentId: string | null) =>
  console.log('content deleted', postId, commentId)

// ForumReportRow
// get either a post or a comment id
// the approved content will not be hidden anymore
const approveReportContent = (postId: string | null, commentId: string | null) =>
  console.log('content approve', postId, commentId)

// ---------------------------
// FORUM UTILITY FUNCTIONS
// For utility like tabs and navigations, not related to backend
// ---------------------------

const getHistoryTabs = (isAdmin: boolean) => {
  return isAdmin ? forumHistoryNavs : forumHistoryNavs.slice(0, 2)
}

const getBookmarkTabs = () => {
  return forumBookmarkNavs
}

const getForumCategories = () => {
  return tempForumCategory.slice(0, 3)
}

const getForumTagCategoryBreadcrumbs = (category: string) => {
  return forumTagCategoryBreadcrumbs.concat({
    title: category,
    link: ''
  })
}

const getClassTabs = (slug: string, isAdmin: boolean) => {
  const tabs = classForumStudentTabs.map((tab) => {
    return {
      ...tab,
      link: tab.link.replaceAll('SLUG_HERE', slug)
    }
  })
  return isAdmin ? tabs : tabs.slice(0, 4)
}

const checkTagSlugExist = (slug: string) => {
  return forumTagSlugs.find((tag) => tag === slug)
}

const paginatePosts = (page: number, posts: ForumPost[]) => {
  const size = 6
  const total = Math.ceil(posts.length / size) // total pages
  const data = posts.slice((page - 1) * size, page * size)

  return { page, total, data }
}

const paginateAnnouncements = (page: number, posts: ForumPost[]) => {
  const size = 8
  const total = Math.ceil(posts.length / size) // total pages
  const data = posts.slice((page - 1) * size, page * size)

  return { page, total, data }
}

const paginateUsers = (page: number, users: ForumAuthor[]) => {
  const size = 8
  const total = Math.ceil(users.length / size) // total pages
  const data = users.slice((page - 1) * size, page * size)

  return { page, total, data }
}

// handle sidebar tags
const sidebarTagsKey = 'forum_sidebar_tags'
const sidebarClassKey = 'forum_sidebar_classes'
const sidebarTypeKey = 'forum_has_following'

const addStorageTags = (data: string) => {
  if (localStorage) localStorage.setItem(sidebarTagsKey, data)
}

const removeStorageTags = () => {
  if (localStorage) localStorage.removeItem(sidebarTagsKey)
}

const getStorageTags = () => {
  if (localStorage) {
    const data = localStorage.getItem(sidebarTagsKey)
    return data || false
  }
  return false
}

const addStorageClass = (data: string) => {
  if (localStorage) localStorage.setItem(sidebarClassKey, data)
}

const removeStorageClass = () => {
  if (localStorage) localStorage.removeItem(sidebarClassKey)
}

const getStorageClass = () => {
  if (localStorage) {
    const data = localStorage.getItem(sidebarClassKey)
    return data || false
  }
  return false
}

const getStorageTagsType = () => {
  if (localStorage) {
    const data = localStorage.getItem(sidebarTypeKey)
    return data ? data === 'true' : false
  }
  return false
}

const addStorageTagsType = (hasFollowing: boolean) => {
  if (localStorage) localStorage.setItem(sidebarTypeKey, hasFollowing ? 'true' : 'false')
}

const handleStorageTagsAfterFollow = () => {
  // check if previous has no followed tags
  if (!getStorageTagsType()) {
    // change following type to true
    addStorageTagsType(true)
    // remove previous tags
    removeStorageTags()
  }

  // check if has data from storage
  const data = getStorageTags()
  // if has data, check if data is less than three
  if (data) {
    const parsed = JSON.parse(data)
    // if data is less than three, remove storage and it will get updated later
    if (parsed.length < 3) removeStorageTags()
  }
}

const handleStorageTagsAfterUnfollow = (tagSlug: string) => {
  // check if has data from storage
  const data = getStorageTags()
  // if has data, check if the unfollowed tags is in storage data
  if (data) {
    const parsed = JSON.parse(data)
    const tagIndex = parsed.findIndex((tag: any) => tag.slug_name === tagSlug)
    // if tags is present
    if (tagIndex > -1) {
      removeStorageTags()
      // if only has one tag left, ensure tag type is false afterwards
      if (parsed.length === 1) addStorageTagsType(false)
    }
  }
}

const removeForumStorageData = () => {
  if (localStorage) {
    localStorage.removeItem(sidebarTagsKey)
    localStorage.removeItem(sidebarClassKey)
    localStorage.removeItem(sidebarTypeKey)
  }
}

// ---------------------------
// LEARN COURSES FUNCTIONS
// ---------------------------
// TEMPORARY FUNCTIONS

const getClassModulesFromCourseId = (courseId: string) => {
  console.log(courseId)
  return tempClassModules
}

const getClassGradesFromCourseId = (courseId: string) => {
  console.log(courseId)
  return tempClassModules.map((mod) => {
    return {
      ...mod,
      lessons: mod.lessons.filter((lesson) => classGradeLessonTypes.includes(lesson.type))
    }
  })
}

// ---------------------------
// INTERVIEWS
// ---------------------------

const getInterviewFeedbackScoreData = (key: InterviewFeedbackCategoryTitle) => {
  return interviewFeedbackCategoriesScore[key]
}

const getInterviewGeneralTermsAndConditions = () => {
  return interviewTermsAndConditions
}

const getTestGeneralTermsAndConditions = () => {
  return testTermsAndConditions
}

const getCourseBadgeFileName = (courseName: string) => {
  if (courseName.includes('Fundamentals of PHP')) {
    return 'php.png'
  }
  if (courseName.includes('Fintech')) {
    return 'fif.png'
  } else if (
    courseName.includes('FC') ||
    courseName.includes('Foundation Certificate') ||
    courseName.includes('Fundamental')
  ) {
    if (courseName.includes('Data Analytics')) {
      return 'fcda.png'
    } else if (courseName.includes('Software Engineering')) {
      return 'fcse.png'
    } else if (courseName.includes('Technology Management')) {
      return 'fctm.png'
    } else if (courseName.includes('Data-driven')) {
      return 'fcddm.png'
    } else if (courseName.includes('Digital Marketing')) {
      return 'fcdm.png'
    } else if (courseName.includes('UX')) {
      return 'fcuiux.png'
    } else if (courseName.includes('Tech Sales')) {
      return 'fcts.png'
    } else {
      return 'fcse.png'
    }
  } else if (courseName.includes('CA') || courseName.includes('Certified Associate')) {
    if (courseName.includes('Software Engineering')) return 'case.png'
    else if (courseName.includes('Front-end Development')) return 'cafed.png'
    else if (courseName.includes('Back-end Development')) return 'cabed.png'
    else if (courseName.includes('Business Analytics')) return 'caba.png'
    else if (courseName.includes('Data Analytics')) return 'cada.png'
    else if (courseName.includes('Data Science')) return 'cads.png'
    else if (courseName.includes('Penetration Testing')) return 'capt.png'
    else if (courseName.includes('Prompt Engineering')) return 'cape.png'
    else if (courseName.includes('Machine Learning')) return 'caml.png'
    else if (courseName.includes('Artificial Intelligence')) return 'caai.png'
    else return 'cada.png'
  } else if (courseName.includes('M1')) {
    if (courseName.includes('Python')) {
      return 'm1py.png'
    } else if (courseName.includes('SQL')) {
      return 'm1sql.png'
    } else if (courseName.includes('HTML')) {
      return 'm1-html-css.png'
    } else if (courseName.includes('AI')) {
      return 'm1ai.png'
    } else {
      return 'm1js.png'
    }
  } else if (courseName.includes('Penetration Testing')) {
    return 'hacker.png'
  } else if (courseName.includes('Block')) {
    return 'blockchain.png'
  } else if (courseName.includes('Webinar')) {
    return 'webinar.png'
  } else if (courseName.includes('Python') && courseName.includes('Data')) {
    return 'python.png'
  } else if (courseName.includes('Python')) {
    return 'python.png'
  } else if (courseName.includes('SQL')) {
    return 'sql.png'
  } else if (courseName.includes('HTML')) {
    return 'html-css.png'
  } else if (courseName.includes('js') || courseName.toLowerCase().includes('javascript')) {
    return 'js.png'
  } else if (courseName.includes('React')) {
    return 'logo-react.png'
  } else if (courseName.includes('Express')) {
    return 'express.png'
  } else if (courseName.includes('Software Engineering')) {
    return 'software.png'
  } else if (
    courseName.includes('Career') ||
    courseName.includes('CV') ||
    courseName.includes('Resume') ||
    courseName.includes('MySTEP+')
  ) {
    return 'logo-workshop-career.png'
  } else if (courseName.includes('STEAM')) {
    return 'cyberport.png'
  } else if (courseName.includes('Orientation')) {
    return 'logo-orientation.png'
  } else if (courseName.includes('Digital Technology')) {
    return 'logo-digital-tech.png'
  } else if (courseName.includes('Technology Trend')) {
    return 'logo-technology.png'
  } else if (courseName.includes('Fundamentals of Technology Management')) {
    return 'logo-link.png'
  } else if (courseName.includes('Technology & Management Fundamentals')) {
    return 'logo-computer-building.png'
  } else if (
    courseName.includes('Introduction to Digital Marketing') ||
    courseName.includes('Overview on Digital Marketing')
  ) {
    return 'digital-marketing.png'
  } else if (courseName.includes('Marketing Channel') || courseName.includes('Ads Placement')) {
    return 'marketing-channels.png'
  } else if (
    courseName.includes('Measuring Effectiveness') ||
    courseName.includes('Measurement Performance')
  ) {
    return 'logo-marketing-analysis.png'
  } else if (courseName.includes('Marketing Plan')) {
    return 'logo-timetable.png'
  } else if (
    courseName.includes('Algo') ||
    courseName.includes('AI') ||
    courseName.includes('Machine Learning') ||
    courseName.includes('Advanced Analytics')
  ) {
    return 'neural.png'
  } else if (courseName.includes('Coding')) {
    return 'coding.png'
  } else if (courseName.includes('Web')) {
    return 'internet.png'
  } else if (courseName.includes('Data Collection')) {
    return 'data-collection.png'
  } else if (courseName.includes('Preparing data')) {
    return 'prepare-data.png'
  } else if (courseName.includes('Capstone') || courseName.includes('Project')) {
    return 'project-management.png'
  } else if (
    courseName.includes('Data Analysis') ||
    courseName.includes('Data Analytics') ||
    courseName.includes('Data Science')
  ) {
    return 'data-1.png'
  } else if (courseName.includes('Data Visual')) {
    return 'data-visual.png'
  } else if (courseName.includes('Stat')) {
    return 'curve.png'
  } else if (courseName.includes('Predict')) {
    return 'prediction.png'
  } else if (courseName.includes('ETL')) {
    return 'data-transformation.png'
  } else if (courseName.toLowerCase().includes('cyberport')) {
    return 'cyberport.png'
  } else if (courseName.toLowerCase().includes('google')) {
    return 'google.png'
  } else {
    return 'else.png'
  }
  console.log('courseName', courseName)
}

// ---------------------------

type useStaticDataReturnType = {
  getCvScoreWeightage: typeof getCvScoreWeightage
  getClassModulesFromCourseId: typeof getClassModulesFromCourseId
  getInterviewGeneralTermsAndConditions: typeof getInterviewGeneralTermsAndConditions
  getTestGeneralTermsAndConditions: typeof getTestGeneralTermsAndConditions
  getHeroStats: typeof getHeroStats
  getProgrammeStats: typeof getProgrammeStats
  getTrustedCompanies: typeof getTrustedCompanies
  getSolutions: typeof getSolutions
  getCourseById: typeof getCourseById
  listCourses: typeof listCourses
  getTotalCourses: typeof getTotalCourses
  getCourseRoles: typeof getCourseRoles
  getCourseRolesById: typeof getCourseRolesById
  getCourseRoleTitle: typeof getCourseRoleTitle
  getCourseProviderTitle: typeof getCourseProviderTitle
  getCourseProviderTitleByCourseId: typeof getCourseProviderTitleByCourseId
  getCourseTypeById: typeof getCourseTypeById
  getCourseTypeTitle: typeof getCourseTypeTitle
  getBadgeByCourseName: typeof getBadgeByCourseName
  getCourseByName: typeof getCourseByName
  getTestimonies: typeof getTestimonies
  getTestimoniesByIds: typeof getTestimoniesByIds
  getPrograms: typeof getPrograms
  getProgramById: typeof getProgramById
  getProgramPartnerTitle: typeof getProgramPartnerTitle
  getCourseToolById: typeof getCourseToolById
  getTeamMembers: typeof getTeamMembers
  getFounders: typeof getFounders
  getTeamMemberById: typeof getTeamMemberById
  getCourseFAQs: typeof getCourseFAQs
  getEnrolmentsByCourseId: typeof getEnrolmentsByCourseId
  getJobById: typeof getJobById
  listJobs: typeof listJobs
  getEmployerById: typeof getEmployerById
  getExclusiveJobCampaigns: typeof getExclusiveJobCampaigns
  getAllActiveJobCampaigns: typeof getAllActiveJobCampaigns
  getSimilarJobs: typeof getSimilarJobs
  getEmployerJobs: typeof getEmployerJobs
  getFeaturedJobs: typeof getFeaturedJobs
  getFeaturedEvents: typeof getFeaturedEvents
  listEvents: typeof listEvents
  getEventById: typeof getEventById
  getCertificateById: typeof getCertificateById
  getStudentById: typeof getStudentById
  getJobSidebarLinks: typeof getJobSidebarLinks
  getProgrammeSidebarLinks: typeof getProgrammeSidebarLinks
  getStudentSidebarLinks: typeof getStudentSidebarLinks
  getForumSidebarLinks: typeof getForumSidebarLinks
  getSavedJobs: typeof getSavedJobs
  getAppliedJobs: typeof getAppliedJobs
  checkSavedJob: typeof checkSavedJob
  checkAppliedJob: typeof checkAppliedJob
  getResume: typeof getResume
  getRankTestById: typeof getRankTestById
  getRankingsWithoutDuplicates: typeof getRankingsWithoutDuplicates
  rearrangeSkillsMap: typeof rearrangeSkillsMap
  getLocationOptions: typeof getLocationOptions
  getRegionOptions: typeof getRegionOptions
  getSkillsOptions: typeof getSkillsOptions
  mapToolToTitle: typeof mapToolToTitle
  mapToolToId: typeof mapToolToId
  getStudentChosenTools: typeof getStudentChosenTools
  getWorkExperienceById: typeof getWorkExperienceById
  getEduBackgroundById: typeof getEduBackgroundById
  getHighestRanking: typeof getHighestRanking
  getCertificatesListById: typeof getCertificatesListById
  calculateProfileCompletion: typeof calculateProfileCompletion
  getEmploymentStatusOptions: typeof getEmploymentStatusOptions
  mapCertificates: typeof mapCertificates
  listCertificates: typeof listCertificates
  removeEmptyArrayItems: typeof removeEmptyArrayItems
  getLanguagesOptions: typeof getLanguagesOptions
  getNotificationsData: typeof getNotificationsData
  getNotiContent: typeof getNotiContent
  isNotiReportType: typeof isNotiReportType
  getNameInNoti: typeof getNameInNoti
  getImageUrlInNoti: typeof getImageUrlInNoti
  getPostTitleInNoti: typeof getPostTitleInNoti
  getNotiPageLink: typeof getNotiPageLink
  replaceNotiContent: typeof replaceNotiContent
  getFeedFollowingPosts: typeof getFeedFollowingPosts
  getFeedForYouPosts: typeof getFeedForYouPosts
  setPostAsPinned: typeof setPostAsPinned
  setPostAsUnpinned: typeof setPostAsUnpinned
  getHistoryTabs: typeof getHistoryTabs
  getBookmarkTabs: typeof getBookmarkTabs
  getForumCategories: typeof getForumCategories
  getBrowseTagsData: typeof getBrowseTagsData
  followTag: typeof followTag
  unfollowTag: typeof unfollowTag
  getForumTagCategoryBreadcrumbs: typeof getForumTagCategoryBreadcrumbs
  getTagsFromCategory: typeof getTagsFromCategory
  getTagCategoryFromName: typeof getTagCategoryFromName
  getSidebarClass: typeof getSidebarClass
  getSidebarTags: typeof getSidebarTags
  getTagFromName: typeof getTagFromName
  getPostsFromTag: typeof getPostsFromTag
  getClassPostsFromTag: typeof getClassPostsFromTag
  getFollowingTags: typeof getFollowingTags
  getJoinedClassForums: typeof getJoinedClassForums
  getClassAnnouncements: typeof getClassAnnouncements
  getRecentClassAnnouncements: typeof getRecentClassAnnouncements
  getClassTabs: typeof getClassTabs
  getPinnedPosts: typeof getPinnedPosts
  getRecentAdminClasses: typeof getRecentAdminClasses
  getAllAdminClasses: typeof getAllAdminClasses
  getAllForumReports: typeof getAllForumReports
  deleteReportContent: typeof deleteReportContent
  approveReportContent: typeof approveReportContent
  getClassForumStudents: typeof getClassForumStudents
  getClassForumModerators: typeof getClassForumModerators
  removeStudentFromClass: typeof removeStudentFromClass
  addStudentToClass: typeof addStudentToClass
  addModeratorToClass: typeof addStudentToClass
  removeModeratorFromClass: typeof removeModeratorFromClass
  getForumPostFromId: typeof getForumPostFromId
  filterModeratorsByName: typeof filterModeratorsByName
  filterStudentsByName: typeof filterStudentsByName
  addManyStudentToClass: typeof addManyStudentToClass
  addManyModeratorsToClass: typeof addManyModeratorsToClass
  getClassFromSlug: typeof getClassFromSlug
  searchForum: typeof searchForum
  setPostAsResolved: typeof setPostAsResolved
  setPostAsUnresolved: typeof setPostAsUnresolved
  markCommentAsAnswer: typeof markCommentAsAnswer
  removeCommentAsAnswer: typeof removeCommentAsAnswer
  deleteForumPost: typeof deleteForumPost
  deleteForumComment: typeof deleteForumComment
  getClassAnnouncementFromId: typeof getClassAnnouncementFromId
  getCommentFromId: typeof getCommentFromId
  getAllClassForumModerators: typeof getAllClassForumModerators
  getAllClassForumStudents: typeof getAllClassForumStudents
  getStudentNavbarLinks: typeof getStudentNavbarLinks
  getActivePrograms: typeof getActivePrograms
  getDashboardCourses: typeof getDashboardCourses
  getDashboardPosts: typeof getDashboardPosts
  getPromotedEvent: typeof getPromotedEvent
  getClassGradesFromCourseId: typeof getClassGradesFromCourseId
  checkTagSlugExist: typeof checkTagSlugExist
  addStorageTags: typeof addStorageTags
  removeStorageTags: typeof removeStorageTags
  getStorageTags: typeof getStorageTags
  addStorageClass: typeof addStorageClass
  removeStorageClass: typeof removeStorageClass
  getStorageClass: typeof getStorageClass
  getStorageTagsType: typeof getStorageTagsType
  addStorageTagsType: typeof addStorageTagsType
  handleStorageTagsAfterFollow: typeof handleStorageTagsAfterFollow
  handleStorageTagsAfterUnfollow: typeof handleStorageTagsAfterUnfollow
  removeForumStorageData: typeof removeForumStorageData
  getEventFormOptions: typeof getEventFormOptions
  getInterviewFeedbackScoreData: typeof getInterviewFeedbackScoreData
  getCourseFromProgram: typeof getCourseFromProgram
  getProgrammeRegistrationFormOptions: typeof getProgrammeRegistrationFormOptions
  getCourseBadgeFileName: typeof getCourseBadgeFileName
}

export function useStaticData(): useStaticDataReturnType {
  return {
    getCvScoreWeightage,
    getClassModulesFromCourseId,
    getInterviewGeneralTermsAndConditions,
    getTestGeneralTermsAndConditions,
    getProgrammeRegistrationFormOptions,
    getClassGradesFromCourseId,
    getPromotedEvent,
    getDashboardPosts,
    getDashboardCourses,
    getActivePrograms,
    getInterviewFeedbackScoreData,
    getStorageTagsType,
    addStorageTagsType,
    addStorageTags,
    removeStorageTags,
    addStorageClass,
    removeStorageClass,
    getStorageClass,
    getStorageTags,
    handleStorageTagsAfterFollow,
    handleStorageTagsAfterUnfollow,
    removeForumStorageData,
    checkTagSlugExist,
    getAllClassForumStudents,
    getAllClassForumModerators,
    getCommentFromId,
    filterModeratorsByName,
    filterStudentsByName,
    addManyModeratorsToClass,
    addManyStudentToClass,
    getClassFromSlug,
    searchForum,
    setPostAsResolved,
    setPostAsUnresolved,
    markCommentAsAnswer,
    removeCommentAsAnswer,
    deleteForumComment,
    getClassAnnouncementFromId,
    getStudentNavbarLinks,
    // landing stuff
    getCourseFromProgram,
    getEventFormOptions,
    getHeroStats,
    getProgrammeStats,
    getTrustedCompanies,
    getSolutions,
    getCourseById,
    getCourseRoles,
    getCourseRolesById,
    getCourseRoleTitle,
    listCourses,
    getTotalCourses,
    getCourseProviderTitle,
    getCourseProviderTitleByCourseId,
    getCourseTypeById,
    getCourseTypeTitle,
    getBadgeByCourseName,
    getCourseByName,
    getTestimonies,
    getTestimoniesByIds,
    getPrograms,
    getProgramById,
    getProgramPartnerTitle,
    getCourseToolById,
    getTeamMembers,
    getFounders,
    getTeamMemberById,
    getCourseFAQs,
    getEnrolmentsByCourseId,
    getJobById,
    listJobs,
    getEmployerById,
    getExclusiveJobCampaigns,
    getAllActiveJobCampaigns,
    getSimilarJobs,
    getEmployerJobs,
    getFeaturedJobs,
    getFeaturedEvents,
    listEvents,
    getEventById,
    getCertificateById,
    getStudentById,
    getJobSidebarLinks,
    getProgrammeSidebarLinks,
    getStudentSidebarLinks,
    getForumSidebarLinks,
    getSavedJobs,
    getAppliedJobs,
    checkSavedJob,
    checkAppliedJob,
    getResume,
    getRankTestById,
    getRankingsWithoutDuplicates,
    rearrangeSkillsMap,
    getLocationOptions,
    getRegionOptions,
    getSkillsOptions,
    mapToolToTitle,
    mapToolToId,
    getStudentChosenTools,
    getWorkExperienceById,
    getEduBackgroundById,
    getHighestRanking,
    getCertificatesListById,
    calculateProfileCompletion,
    getEmploymentStatusOptions,
    mapCertificates,
    listCertificates,
    removeEmptyArrayItems,
    getLanguagesOptions,
    getNotificationsData,
    getNotiContent,
    isNotiReportType,
    getNameInNoti,
    getImageUrlInNoti,
    getPostTitleInNoti,
    getNotiPageLink,
    replaceNotiContent,
    getFeedFollowingPosts,
    getFeedForYouPosts,
    setPostAsPinned,
    setPostAsUnpinned,
    getHistoryTabs,
    getBookmarkTabs,
    getForumCategories,
    getBrowseTagsData,
    followTag,
    unfollowTag,
    getForumTagCategoryBreadcrumbs,
    getTagsFromCategory,
    getTagCategoryFromName,
    getSidebarTags,
    getSidebarClass,
    getTagFromName,
    getPostsFromTag,
    getClassPostsFromTag,
    getFollowingTags,
    getJoinedClassForums,
    getClassAnnouncements,
    getRecentClassAnnouncements,
    getClassTabs,
    getPinnedPosts,
    getRecentAdminClasses,
    getAllAdminClasses,
    getAllForumReports,
    deleteReportContent,
    approveReportContent,
    getClassForumStudents,
    getClassForumModerators,
    removeStudentFromClass,
    addStudentToClass,
    addModeratorToClass,
    removeModeratorFromClass,
    getForumPostFromId,
    deleteForumPost,
    getCourseBadgeFileName
  }
}
